import { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import { ModalConfirm } from '@components/Modal';
import ContactDrawer from '@features/ContactDrawer';
import Button from '@components/Button';
import { Input } from '@components/Input';
import { ImportIcon } from 'assets/images/icons';
import { useTranslation, useThrottleValue, useLocations } from '@hooks';
import { contactsActions, alertActions, channelsActions, labelsActions } from '@actions';
import { contactsService } from '@services';
import { ModalType } from '@constants/modal';
import { handleApiError } from '@helpers/api';
import FiltersPanel from './FiltersPanel';
import ContactsTable from './ContactsTable';
import BulkUploadModal from './BulkUploadModal';
import BulkEditModal from './BulkEditModal';
import { Container, FiltersWrapper, ToolbarWrapper, SearchWrapper } from './styles';

const ContactsPage = () => {
    const { t } = useTranslation();
    const firstRender = useRef(true);
    const isTablet = useMediaQuery('@media (max-width: 1024px)');
    const isMobile = useMediaQuery('@media (max-width: 768px)');

    const [filter, setFilter] = useState({
        companies: [],
        timeframe: { start: null, end: null },
        contactLabels: [],
        searchQuery: '',
    });
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);
    const [pageRequests, setPageRequests] = useState(0);
    const [rowsPerPageRequests, setRowsPerPageRequests] = useState(20);

    const [selectedContacts, setSelectedContacts] = useState({ list: {}, all: false });
    const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
    const [isBulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);

    const language = useSelector(state => state.auth.user?.language);
    const companies = useSelector(state => state.account?.account?.companies || []);
    const { totalCount, contact, data: contacts, contactEditModal } = useSelector(state => state.contacts);
    const { data: labels } = useSelector(state => state.labels);

    const dispatch = useDispatch();
    const { mapLocationsToFormValue } = useLocations();
    const throttledSearch = useThrottleValue(filter.searchQuery, 1000);

    useEffect(() => {
        dispatch(labelsActions.get());
        dispatch(channelsActions.get());
        return () => {
            dispatch(contactsActions.resetContacts());
        };
    }, [dispatch]);

    useEffect(() => {
        setFilter(prev => ({ ...prev, companies: mapLocationsToFormValue() }));
    }, [dispatch]);

    const currentQuery = useMemo(() => {
        return {
            companies: filter.companies.map(item => item.id),
            labels: filter.contactLabels,
            searchQuery: filter.searchQuery,
            from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'dd/MMM/yyyy') : null,
            to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'dd/MMM/yyyy') : null,
        };
    }, [filter]);

    useEffect(() => {
        if (currentQuery.companies.length) {
            if (firstRender.current) {
                dispatch(contactsActions.get(currentQuery, pageRequests, rowsPerPageRequests));
                firstRender.current = false;
            }
        }
    }, [pageRequests, currentQuery, rowsPerPageRequests, dispatch]);

    useEffect(() => {
        if (!firstRender.current) {
            dispatch(contactsActions.get(currentQuery, pageRequests, rowsPerPageRequests));
        }
    }, [throttledSearch]);

    useEffect(() => {
        // INFO: refetch contacts if contact labels are edited
        if (contactEditModal.open) {
            dispatch(contactsActions.get(currentQuery, pageRequests, rowsPerPageRequests));
        }
    }, [contactEditModal.open, labels]);

    const onPageChange = (_, newPage) => {
        setPageRequests(newPage);
        dispatch(contactsActions.get(currentQuery, newPage, rowsPerPageRequests));
    };

    const onRowsPerPageChange = event => {
        setRowsPerPageRequests(parseInt(event.target.value, 10));
        setPageRequests(0);
        dispatch(contactsActions.get(currentQuery, 0, parseInt(event.target.value, 10)));
    };

    const onApplyFilters = () => {
        dispatch(contactsActions.get(currentQuery, 0, rowsPerPageRequests));
        setPageRequests(0);
    };

    const onClickContact = (contactId, type) => {
        if (contactId !== contact?.id) {
            dispatch(contactsActions.getContact(contactId));
        }
        switch (type) {
            case 'delete':
                dispatch(contactsActions.setContactDeleteModal(true));
                break;
            case 'edit':
                dispatch(contactsActions.setContactEditModal({ open: true, type: ModalType.Edit }));
                break;
            case 'view':
                dispatch(contactsActions.setContactDrawer(true));
                break;
            default:
                break;
        }
    };

    const onExportContacts = async () => {
        const exportQuery = {
            ...currentQuery,
            contacts: Object.keys(selectedContacts.list),
            allContacts: selectedContacts.all,
        };
        try {
            const file = await contactsService.exportCSVBulk(exportQuery);
            const anchor = document.createElement('a');
            anchor.style.display = 'none';
            anchor.href = file;
            anchor.download = 'contacts.csv';
            document.body.appendChild(anchor);
            anchor.click();
            window.URL.revokeObjectURL(file);
            anchor.remove();
        } catch (error) {
            handleApiError(error, 'common.exportError');
        }
        setSelectedContacts(prev => ({ ...prev, list: {}, all: false }));
    };

    const onDeleteContacts = async () => {
        const deleteQuery = {
            ...currentQuery,
            contacts: Object.keys(selectedContacts.list),
            allContacts: selectedContacts.all,
        };
        try {
            await contactsService.deleteBulkContacts(deleteQuery);
            await dispatch(contactsActions.get(currentQuery, pageRequests, rowsPerPageRequests));
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail');
        }
        dispatch(contactsActions.setContactEditModal({ open: false, type: ModalType.Create }));
        setIsBulkDeleteModalOpen(false);
        setSelectedContacts(prev => ({ ...prev, list: {}, all: false }));
    };

    const onUpdateCallback = updatedContact => {
        if (contactEditModal.type === ModalType.Create) {
            dispatch(contactsActions.get(currentQuery, 0, rowsPerPageRequests));
        } else {
            const contactsCopy = contacts.slice();
            const updatedContactIndex = contactsCopy.findIndex(c => c.id === contact?.id);
            if (updatedContactIndex !== -1 && updatedContact) {
                contactsCopy[updatedContactIndex] = updatedContact;
                dispatch(contactsActions.setContacts(contactsCopy));
            }
        }
    };

    const onDeleteCallback = () => dispatch(contactsActions.get(currentQuery, pageRequests, rowsPerPageRequests));

    const onMergeCallback = () => dispatch(contactsActions.get(currentQuery, pageRequests, rowsPerPageRequests));

    const onOpenEditModal = () => dispatch(contactsActions.setContactEditModal({ open: true, type: ModalType.Create }));

    const selectedLength = Object.keys(selectedContacts.list).length;

    return (
        <Container>
            <FiltersWrapper>
                <SearchWrapper>
                    <Input
                        inputProps={{
                            value: filter.searchQuery,
                            onChange: e => setFilter(prev => ({ ...prev, searchQuery: e.target.value })),
                        }}
                        label={t(isTablet ? 'ContactsPage.search' : 'ContactsPage.searchLabel')}
                        startAdornment={<SearchIcon />}
                    />
                </SearchWrapper>
                <FiltersPanel
                    type="contacts"
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    onApplyFilters={onApplyFilters}
                    contactLabels={labels}
                    filter={filter}
                    setFilter={setFilter}
                    setFilterNumber={setFilterNumber}
                    withChips={false}
                />
                {!!selectedLength && (
                    <ToolbarWrapper>
                        <Button
                            sx={{ minWidth: 'min-content' }}
                            variant="text"
                            startIcon={<EditIcon />}
                            onClick={() => setIsBulkEditModalOpen(true)}
                        >
                            {!isMobile ? t('buttons.edit') : ''}
                        </Button>
                        <Button
                            sx={{ minWidth: 'min-content' }}
                            variant="text"
                            startIcon={<FileDownloadIcon sx={{ fontSize: '20px', transform: 'translateY(1px)' }} />}
                            onClick={onExportContacts}
                        >
                            {!isMobile ? t('buttons.export') : ''}
                        </Button>
                        <Button
                            sx={{ minWidth: 'min-content' }}
                            variant="text"
                            startIcon={<DeleteIcon />}
                            onClick={() => setIsBulkDeleteModalOpen(true)}
                        >
                            {!isMobile ? t('buttons.delete') : ''}
                        </Button>
                    </ToolbarWrapper>
                )}
                <div style={{ gridArea: 'controls', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    {!isMobile ? (
                        <Button
                            variant="outlined"
                            startIcon={<ImportIcon />}
                            onClick={() => setBulkUploadModalOpen(true)}
                        >
                            {t('buttons.import')}
                        </Button>
                    ) : null}
                    <Button variant="contained" startIcon={<AddIcon />} onClick={onOpenEditModal}>
                        {t('buttons.create')}
                    </Button>
                </div>
            </FiltersWrapper>
            <ContactsTable
                companies={companies}
                onClickContact={onClickContact}
                rowsPerPageRequests={rowsPerPageRequests}
                pageRequests={pageRequests}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                selectedContacts={selectedContacts}
                setSelectedContacts={setSelectedContacts}
            />
            <ContactDrawer
                onDeleteCallback={onDeleteCallback}
                onMergeCallback={onMergeCallback}
                onUpdateCallback={onUpdateCallback}
            />
            {isBulkEditModalOpen ? (
                <BulkEditModal
                    modal={isBulkEditModalOpen}
                    setModal={setIsBulkEditModalOpen}
                    selectedContacts={selectedContacts}
                    currentQuery={currentQuery}
                    rowsPerPageRequests={rowsPerPageRequests}
                />
            ) : null}
            {isBulkUploadModalOpen ? (
                <BulkUploadModal
                    isOpen={isBulkUploadModalOpen}
                    setModal={setBulkUploadModalOpen}
                    locale={language}
                    onUpdateCallback={onUpdateCallback}
                />
            ) : null}
            {isBulkDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isBulkDeleteModalOpen}
                    handleClose={() => setIsBulkDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('ContactsPage.deleteContactsDescription', {
                        selectedContacts: selectedContacts.all ? totalCount : selectedLength,
                    })}
                    onPrimaryAction={onDeleteContacts}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                    onSecondaryAction={() => setIsBulkDeleteModalOpen(false)}
                />
            )}
        </Container>
    );
};

export default ContactsPage;
